import { api } from '@/lib/axios';
import type {
  AuthUser,
  EinrichtungStart,
  LoginCredentials,
  LoginResponse,
  Mitgliedschaft,
  RegistriertesGeraet,
  ZweiFaktorStatus,
  ZweiFaktorVerfahren,
  ZweiterSchritt,
} from '@/lib/auth.types';

export const authApi = {
  /** AP-38-E: Fehlt noch der zweite Faktor oder die Einrichtung, kommt ein Ticket statt eines Tokens. */
  login: (credentials: LoginCredentials) =>
    api.post<LoginResponse | ZweiterSchritt>('/auth/login', credentials),

  // ── Zwei-Faktor-Anmeldung (AP-38-E) ──────────────────────────────────────

  verifyTwoFactor: (ticket: string, code: string, geraetMerken: boolean) =>
    api.post<LoginResponse>('/auth/2fa/verify', { ticket, code, geraetMerken }),

  resendTwoFactor: (ticket: string) =>
    api.post<{ email: string }>('/auth/2fa/resend', { ticket }),

  /** Einrichtung mit Ticket (Pflicht, noch nicht angemeldet). */
  startSetup: (ticket: string, verfahren: ZweiFaktorVerfahren) =>
    api.post<EinrichtungStart>('/auth/2fa/setup', { ticket, verfahren }),

  confirmSetup: (ticket: string, code: string, geraetMerken: boolean) =>
    api.post<LoginResponse>('/auth/2fa/setup/confirm', { ticket, code, geraetMerken }),

  /** Aus dem Profil (angemeldet). */
  twoFactor: () =>
    api.get<{ data: ZweiFaktorStatus & { geraete: RegistriertesGeraet[] } }>('/auth/2fa'),

  startOwnSetup: (verfahren: ZweiFaktorVerfahren, code?: string) =>
    api.post<{ data: EinrichtungStart }>('/auth/2fa/me/setup', { verfahren, ...(code ? { code } : {}) }),

  confirmOwnSetup: (code: string) =>
    api.post<{ data: { recoveryCodes: string[] } }>('/auth/2fa/me/setup/confirm', { code }),

  newRecoveryCodes: (code: string) =>
    api.post<{ data: { recoveryCodes: string[] } }>('/auth/2fa/me/recovery-codes', { code }),

  /** Bestätigungscode per E-Mail — nur beim Verfahren E-Mail. */
  sendOwnCode: () =>
    api.post<{ data: { email: string } }>('/auth/2fa/me/send-code'),

  forgetDevice: (id: string) =>
    api.delete(`/auth/2fa/me/devices/${id}`),

  logout: () =>
    api.post<void>('/auth/logout'),

  /**
   * AP-38-B: mit der Firma dieses Tabs. Die Antwort sagt, für welche Firma der
   * neue Token gilt — weicht sie ab, gilt die gewählte nicht mehr.
   */
  refresh: (tenantId?: string | null) =>
    api.post<{ accessToken: string; tenantId: string }>('/auth/refresh', tenantId ? { tenantId } : {}),

  /** AP-38-B: die Firmen der angemeldeten Person (Dropdown). */
  memberships: () =>
    api.get<{ data: Mitgliedschaft[] }>('/auth/memberships'),

  /** AP-38-B: in eine andere Firma wechseln — neuer Zugriffstoken, gleiche Sitzung. */
  switchFirma: (tenantId: string) =>
    api.post<{ accessToken: string; tenantId: string }>('/auth/switch', { tenantId }),

  me: () =>
    api.get<AuthUser>('/auth/me'),

  forgotPassword: (email: string) =>
    api.post<void>('/auth/forgot-password', { email }),

  resetPassword: (token: string, password: string) =>
    api.post<void>('/auth/reset-password', { token, password }),
};
